import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Col,
  Content,
  FlexboxGrid,
  Form,
  Grid,
  Row,
  SelectPicker,
  Text,
} from 'rsuite';
import PortalHeader from '../../components/layout/PortalHeader';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import LateRenewalNotice from '../../components/renew/LateRenewalNotice';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConfigExpressLegalTitleRequired,
  selectConfigExpressRegistrantOptions,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRenewCertifications,
  selectConfigExpressRenewLateFee,
} from '../../store/slices/config/configSelectors';
import { FLOWS_ENUM } from '../../common/enums';
import { C_DEFAULT_RENEW_CERTIFICATION } from '../../common/consts';
import { checkedValidation, requiredValidation } from '../../common/validation';
import {
  selectRenewIsPaymentRequired,
  selectRenewLicenseInProgress,
  selectSelectedBulkRenewalLicenses,
} from '../../store/slices/renew/renewSelectors';
import { setSignatoryName } from '../../store/slices/payment/paymentSlice';
import { bulkRenewLicenses } from '../../store/slices/renew/renewThunks';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { payNothingRenewal } from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import { updatePermitData } from '../../store/slices/renew/renewSlice';
import { constructMarkup, getAddressWithUnitNumber } from '../../common/utils';
import Notice from '../../components/user-feedback/Notice';
import AnnotatedCheckbox from '../../components/form/AnnotatedCheckbox';
import FormTextField from '../../components/form/FormTextField';
import dayjs from 'dayjs';
import IconIndicator from '../../components/user-feedback/IconIndicator';

const BulkRenewConfirmation = () => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();

  const form = useRef();
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});
  const [selectedSignatoryTitle, setSelectedSignatoryTitle] = useState('');
  const [, setIsValidating] = useState(false);
  const [, setHasPayNothingError] = useState(false);
  const [, setProcessingPayNothing] = useState(false);

  const isPerformingRenew = useSelector(selectRenewLicenseInProgress);

  const selectedPreSelectedBulkRenewalLicenses = useSelector(
    selectSelectedBulkRenewalLicenses
  );
  const selectedEmailAddress = useSelector(selectCurrentUserEmail);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedPermitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const selectedCertifications = useSelector(
    selectConfigExpressRenewCertifications
  );
  const lateRenewalFee = useSelector(selectConfigExpressRenewLateFee);
  const selectedPaymentRequired = useSelector(selectRenewIsPaymentRequired);
  const selectedRegistrantOptions = useSelector(
    selectConfigExpressRegistrantOptions
  );
  const selectedLegalTitleRequired = useSelector(
    selectConfigExpressLegalTitleRequired
  );

  const displayCertifications = useMemo(() => {
    if (selectedCertifications && selectedCertifications.length > 0) {
      return selectedCertifications;
    } else {
      return [C_DEFAULT_RENEW_CERTIFICATION];
    }
  }, [selectedCertifications]);

  const formModel = useMemo(() => {
    let certificationMap = {};
    if (displayCertifications) {
      displayCertifications?.forEach(
        (c, idx) => (certificationMap[`certification_${idx}`] = false)
      );
    }
    return {
      signatory_title: '',
      signature: '',
      date: '',
      ...certificationMap,
    };
  }, [displayCertifications]);

  const validationModel = useMemo(() => {
    let certificationValidationMap = {};
    if (displayCertifications) {
      displayCertifications?.forEach(
        (c, idx) =>
          (certificationValidationMap[`certification_${idx}`] =
            checkedValidation)
      );
    }
    return {
      signature: requiredValidation,
      ...certificationValidationMap,
    };
  }, [displayCertifications]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  const validateCertifications = () => {
    if (!form.current?.check()) return false;
    return true;
  };

  const validateSignatoryTitle = () => {
    return Object.keys(selectedRegistrantOptions)
      .map((k) => registrantOptionNameMapping(k).toLowerCase())
      .includes(selectedSignatoryTitle);
  };

  const prepareMandatoryAcknowledgementsObject = () => {
    let mandatoryAcknowledgements = {};
    let count = 1;
    for (const certification of displayCertifications) {
      mandatoryAcknowledgements[`aff_${count}`] = {
        label: certification,
        value: formValues[`certification_${count - 1}`],
      };
      count++;
    }
    return mandatoryAcknowledgements;
  };

  const dispatchConfirmation = () => {
    if (isPerformingRenew) return;
    setIsValidating(true);
    if (
      !validateCertifications() ||
      (selectedLegalTitleRequired && !validateSignatoryTitle())
    ) {
      setIsValidating(false);
      return;
    }
    setIsValidating(false);
    const mandatoryAcknowledgements = prepareMandatoryAcknowledgementsObject();
    const certifications = {
      mandatoryAcknowledgements: { ...mandatoryAcknowledgements },
      signatory: formValues.signature,
    };
    if (selectedLegalTitleRequired)
      certifications.signatory_title = selectedSignatoryTitle;
    dispatch(
      updatePermitData({
        certifications: certifications,
      })
    );
    dispatch(setSignatoryName(formValues.signatory));
    dispatch(bulkRenewLicenses(finalizeOnNext));
  };

  const finalizeOnNext = (wasSuccessful, requiresPayment) => {
    // If successfull go to the success page, else error messages will be displayed
    if (wasSuccessful) {
      if (requiresPayment) {
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/renew-payment', { state: { isBulkRenew: true } });
      } else {
        setProcessingPayNothing(true);
        dispatch(
          payNothingRenewal(selectedEmailAddress, onPayNothingCompleted)
        );
      }
    }
  };

  const onPayNothingCompleted = (successful) => {
    setHasPayNothingError(successful);
    setProcessingPayNothing(false);
    if (successful) {
      placeNavigate('/renew-success', { state: { isBulkRenew: true } });
    }
  };

  const registrantOptionNameMapping = (optionKey) => {
    switch (optionKey) {
      case 'property_owner':
        return 'Owner';
      case 'property_manager':
        return 'Manager';
      case 'tenant':
        return 'Tenant';
    }
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.BULK_RENEWAL} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Confirm and Sign"
            onNext={dispatchConfirmation}
          >
            <Form
              className="mt-12"
              ref={form}
              checkTrigger="blur"
              formDefaultValue={formModel}
              onCheck={setFormError}
              onChange={(f) => {
                setFormValues({ ...formModel, ...f });
              }}
              fluid
            >
              <FlexboxGrid
                className="mt-12 ml-0"
                xs={24}
                lg={12}
              >
                <FlexboxGrid.Item colspan={24}>
                  <div role="grid">
                    <Row>
                      <Col xs={24}>
                        {selectedPaymentRequired ? (
                          <Text className="mb-16">
                            Please confirm the information below to proceed to
                            the payment step.
                          </Text>
                        ) : (
                          <Text className="mb-16">
                            Please confirm the following information and sign
                            below.
                          </Text>
                        )}
                        <Text className="mb-16">
                          A {selectedPermitTypeVerbiage} is valid for one year
                          from the date of issuance and must be reviewed
                          annually. For all permits renewed today, the new
                          expiration date will be 1 year from the previous
                          expiration date.
                        </Text>
                        <Text>
                          Please note that back processor fees will be added to
                          the total paid. If paying by ACH the fee is $1.50 +
                          0.8% of the total (cappted at a maximum total fee of
                          $6.50). If paying by credit card the fee is $0.30 plus
                          2.9% of the total.
                        </Text>
                      </Col>
                    </Row>
                  </div>
                  {lateRenewalFee && <LateRenewalNotice className="mb-24" />}
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={24}>
                  {selectedPreSelectedBulkRenewalLicenses?.length > 0 ? (
                    <div
                      className="mv-24"
                      key={`bulk_renewal_table`}
                    >
                      <Grid
                        fluid
                        className="tax-table"
                      >
                        <FlexboxGrid
                          className="tax-column-heading-row"
                          align="bottom"
                          as={Row}
                        >
                          <FlexboxGrid.Item
                            as={Col}
                            xs={24}
                            xsHidden
                            smHidden
                            mdHidden={false}
                          >
                            <div role="grid">
                              <FlexboxGrid
                                as={Row}
                                align="bottom"
                              >
                                <FlexboxGrid.Item
                                  as={Col}
                                  xs={22}
                                  md={9}
                                >
                                  Address
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  as={Col}
                                  xs={12}
                                  md={5}
                                >
                                  {selectedPermitTypeVerbiage} Number
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  as={Col}
                                  xs={6}
                                  md={5}
                                >
                                  Expiration Date
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                  as={Col}
                                  xs={6}
                                  md={5}
                                >
                                  Owner's Name
                                </FlexboxGrid.Item>
                              </FlexboxGrid>
                            </div>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                        <div className="tax-license-row-wrapper">
                          {selectedPreSelectedBulkRenewalLicenses?.map(
                            (license, index) => (
                              <Row
                                key={`license_${index}`}
                                className="tax-license-row"
                              >
                                <Col
                                  xs={24}
                                  md={24}
                                >
                                  <div role="grid">
                                    <Row>
                                      <Col
                                        xs={22}
                                        md={9}
                                        className="select-properties-address"
                                      >
                                        {getAddressWithUnitNumber(
                                          license?.property_address,
                                          license?.unit_number
                                        )}
                                      </Col>
                                      <Col
                                        xs={12}
                                        md={5}
                                      >
                                        {license?.license}
                                      </Col>
                                      <Col
                                        xs={12}
                                        md={5}
                                      >
                                        {license?.expiration_date}
                                      </Col>
                                      <Col
                                        xs={24}
                                        md={5}
                                      >
                                        {license?.owner}
                                      </Col>
                                    </Row>
                                  </div>
                                </Col>
                              </Row>
                            )
                          )}
                        </div>
                      </Grid>
                    </div>
                  ) : (
                    <Notice
                      message="No renewable user licenses available."
                      type="info"
                    />
                  )}
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={24}>
                  <Grid fluid>
                    <Row>
                      <Col>
                        <h2>Certifications</h2>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {displayCertifications?.map((certificaton, idx) => {
                          return (
                            <AnnotatedCheckbox
                              style={{ marginTop: 0, marginBottom: '16px' }}
                              key={`certification_${idx}`}
                              label={constructMarkup(certificaton)}
                              hasError={formError[`certification_${idx}`]}
                              name={`certification_${idx}`}
                              validationRule={
                                validationModel[`certification_${idx}`]
                              }
                            />
                          );
                        })}
                      </Col>
                    </Row>
                  </Grid>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={24}>
                  <Grid fluid>
                    <Row>
                      <Col>
                        <h2>Electronic Signature</h2>
                      </Col>
                    </Row>
                    {selectedLegalTitleRequired && (
                      <>
                        <Row>
                          <Col>
                            <Text>Legal Title of Authorized Signatory</Text>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12}>
                            <SelectPicker
                              name="signatory_title"
                              data={Object.keys(selectedRegistrantOptions)?.map(
                                (item) => ({
                                  label: registrantOptionNameMapping(item),
                                  value:
                                    registrantOptionNameMapping(
                                      item
                                    ).toLowerCase(),
                                })
                              )}
                              placeholder="Select..."
                              searchable={false}
                              style={{ width: 224 }}
                              onChange={(v) => {
                                setSelectedSignatoryTitle(v);
                              }}
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row
                      className="mt-16"
                      style={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Col xs={12}>
                        <FormTextField
                          label="Legal Name of Authorized Signatory"
                          validationRule={validationModel.signature}
                          hasError={formError['signature']}
                          name="signature"
                          errorMessage="Enter your legal name to sign"
                          autoComplete="name"
                        />
                      </Col>
                      <Col xs={12}>
                        <FormTextField
                          value={dayjs().format('MM/DD/YYYY')}
                          label="Date"
                          name="date"
                          disabled={true}
                        />
                      </Col>
                    </Row>
                  </Grid>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Form>
            {isPerformingRenew && <IconIndicator className="mt-16" />}
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default BulkRenewConfirmation;
